import { CSSProperties, FC, useEffect, useState } from 'react';
import { CircadianPhase } from '@demind-inc/core';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import './TeamDayEnergyLanes.scss';
import { circadianPhaseInfo, PeakDipPhase, PeakDipStartEndSet } from '../../../data-access';
import { getGutterEnergyZoneBlockStyle } from '../../Energy/energyZoneStyles';
import { HOUR_BLOCK_HEIGHT } from '../../CalendarView/CalendarTimeline/constants';
import {
  getRoundDownDateTime,
  getRoundUpDateTime,
} from '../../CalendarView/CalendarTimeline/helpers';
import { getTeamDayPhaseDateTime } from '../utils';

export interface TeamDayEnergyLaneResource {
  id: string;
  isCalendarShared: boolean;
  isEnergyShared: boolean;
  phases: PeakDipStartEndSet | null;
  timezone?: string;
}

interface TeamDayEnergyLanesProps {
  resources: TeamDayEnergyLaneResource[];
  containerRef: React.RefObject<HTMLDivElement | null>;
}

const PEAK_DIP_PHASES: PeakDipPhase[] = [
  'wakeup_low',
  'morning_high',
  'morning_low',
  'evening_high',
  'evening_low',
];

const LABEL_WIDTH = 120;

const buildZoneStyle = (
  startTime: string,
  endTime: string,
  timezone?: string
): CSSProperties | null => {
  const parsedStartTime = getTeamDayPhaseDateTime(startTime, timezone);
  const parsedEndTime = getTeamDayPhaseDateTime(endTime, timezone);

  if (!parsedStartTime || !parsedEndTime) {
    return null;
  }

  const roundedStartTime = getRoundUpDateTime(parsedStartTime);
  const roundedEndTime = getRoundDownDateTime(parsedEndTime);
  const duration = roundedEndTime.diff(roundedStartTime, 'minutes');

  if (duration <= 0) {
    return null;
  }

  const startTimeHour = roundedStartTime.hour() + roundedStartTime.minute() / 60;

  return {
    top: `${Math.round(startTimeHour * HOUR_BLOCK_HEIGHT)}px`,
    height: `${Math.round((duration / 60) * HOUR_BLOCK_HEIGHT)}px`,
  };
};

const TeamDayEnergyLanes: FC<TeamDayEnergyLanesProps> = ({ resources, containerRef }) => {
  const { t } = useTranslation();
  const [daySlots, setDaySlots] = useState<HTMLElement[]>([]);

  useEffect(() => {
    const root = containerRef.current;

    if (!root) {
      return undefined;
    }

    const refresh = () => {
      const slots = Array.from(
        root.querySelectorAll<HTMLElement>('.rbc-time-content > .rbc-day-slot')
      );
      setDaySlots(slots);
    };

    refresh();

    const observer = new MutationObserver(refresh);
    observer.observe(root, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [containerRef, resources.length]);

  if (!daySlots.length) {
    return null;
  }

  return (
    <>
      {resources.map((resource, resourceIndex) => {
        const slot = daySlots[resourceIndex];

        if (!slot || !resource.isCalendarShared || !resource.isEnergyShared || !resource.phases) {
          return null;
        }

        const sharedPhases = resource.phases;

        return createPortal(
          <div className="team-day-energy-lanes__column" aria-hidden="true">
            {PEAK_DIP_PHASES.filter((phase) => {
              const phaseRange = sharedPhases[phase];
              return !!phaseRange?.start && !!phaseRange?.end;
            }).map((phase) => {
              const phaseRange = sharedPhases[phase]!;
              const phaseInfo = circadianPhaseInfo[phase as CircadianPhase];

              const style = buildZoneStyle(
                phaseRange.start,
                phaseRange.end,
                phaseRange.timezone || resource.timezone
              );

              if (!style || !phaseInfo) {
                return null;
              }

              const zoneHeight = Number.parseInt(style.height?.toString() || '0', 10);
              const labelWidth = Number.isFinite(zoneHeight)
                ? Math.max(0, Math.min(zoneHeight - 5, LABEL_WIDTH))
                : LABEL_WIDTH;

              const zoneBlockStyle = getGutterEnergyZoneBlockStyle(phaseInfo.color);

              return (
                <div
                  key={phase}
                  className="team-day-energy-lanes__zone"
                  style={{
                    ...style,
                    ...zoneBlockStyle,
                  }}
                >
                  <span
                    className="team-day-energy-lanes__label energy-zone-block__label"
                    style={{
                      backgroundColor: phaseInfo.color,
                      maxWidth: labelWidth,
                    }}
                  >
                    {t(`calendar.energyPhase.labels.${phase}`, {
                      defaultValue: phaseInfo.label,
                    })}
                    {phaseInfo.labelIcon}
                  </span>
                </div>
              );
            })}
          </div>,
          slot,
          `team-day-energy-lane-${resource.id}`
        );
      })}
    </>
  );
};

export default TeamDayEnergyLanes;
